import React, { useState, useCallback } from 'react';
import { Layout, Button, Typography, Drawer, message, Tooltip, Avatar } from 'antd';
import {
  LogoutOutlined,
  MenuOutlined,
  FireFilled,
  DoubleLeftOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setActiveStore, setRolePermissions, useIsMobile } from 'sama-shared';
import { useLazyGetRolePermissionsQuery } from 'sama-shared';
import type { RootState } from 'sama-shared';
import { ImpersonationBanner } from 'sama-shared';
import { ExchangeRateHeaderWidget } from 'sama-shared';
import { StoreStatusPill } from '../../../components/ui/StoreStatusPill';
import { ThemeSwitcher } from '../../../components/ui/ThemeSwitcher';
import { useBrasa } from '../../../components/ui/brasaTheme';
import { StoreSelector } from 'sama-shared';
import { UserProfileDropdown } from 'sama-shared';
import { NavigationMenu } from 'sama-shared';
import { useNavigationMenu } from '../hooks/useNavigationMenu';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

/*
 * Intent:     operador que alterna cocina↔panel; el chrome debe orientar sin
 *             robar atención: marca viva arriba, nav densa, salida abajo.
 * Hierarchy:  logo brasa (focal del sidebar) → nav → logout demotido.
 *             Header: sede (focal) → widgets → tema/perfil.
 * Palette:    carbón cálido en dark, crema en light; brasa solo en marca,
 *             activo de nav y glow del logo.
 * Depth:      sidebar = mismo canvas + borde whisper (no "mundo aparte").
 *             Header sticky con blur. Depth por tono, no por sombras duras.
 * Typography: Sora en marca · Inter 13–14 en nav · tabular en cifras.
 * Spacing:    base 4 · sidebar 240px (sirve al contenido) · header 64px.
 */

export const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const b = useBrasa();

  const partner = useSelector((state: RootState) => state.auth.partner);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const allowedStores = useSelector((state: RootState) => state.auth.allowedStores);
  const activeStoreId = useSelector((state: RootState) => state.auth.activeStoreId);

  const [triggerGetPermissions, { isFetching: isSwitchingStore }] =
    useLazyGetRolePermissionsQuery();

  const handleLogout = useCallback(() => {
    dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate]);

  const handleSwitchStore = useCallback(
    async (storeId: string) => {
      if (storeId === activeStoreId) return;
      try {
        if (triggerGetPermissions) {
          const res = await triggerGetPermissions(storeId).unwrap();
          // Activa la sede Y sus permisos juntos: si falta setActiveStore,
          // el header y todas las vistas siguen mostrando la sucursal anterior.
          dispatch(setActiveStore(storeId));
          dispatch(setRolePermissions({ role: res.role, permissions: res.permissions }));
          message.success(
            `Sucursal cambiada a: ${allowedStores.find((s) => s.id === storeId)?.name}`
          );
        }
      } catch (err: any) {
        message.error(err?.data?.message || 'Error al cambiar de sucursal.');
      }
    },
    [activeStoreId, allowedStores, triggerGetPermissions, setRolePermissions, message]
  );

  const brandInitial = (partner?.name || 'S').trim().charAt(0).toUpperCase();

  // El control de colapso vive en la marca (no se usa el trigger de AntD:
  // así el footer de logout es flujo flex normal y nunca puede quedar tapado).
  const renderBrand = (mini: boolean, collapseControl = true) => (
    <div
      style={{
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: mini ? 'center' : 'flex-start',
        gap: 10,
        padding: mini ? '10px 0' : '0 8px 0 16px',
        borderBottom: `1px solid ${b.lineSoft}`,
        flexShrink: 0,
        flexDirection: mini ? 'column' : 'row',
      }}
    >
      <Avatar
        shape="square"
        size={mini ? 34 : 36}
        style={{
          background: 'linear-gradient(135deg, #ff5a00 0%, #ff7a1a 50%, #ffb020 100%)',
          boxShadow: '0 6px 20px -6px rgba(255,90,0,0.65), inset 0 1px 0 rgba(255,255,255,0.35)',
          fontFamily: "'Sora', 'Inter', sans-serif",
          fontWeight: 800,
          fontSize: 17,
          borderRadius: 11, // concéntrico dentro de header 64
          flexShrink: 0,
        }}
      >
        {mini ? <FireFilled style={{ fontSize: 17 }} /> : brandInitial}
      </Avatar>
      {!mini && (
        <div style={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
          <Text
            strong
            className="sama-display"
            style={{
              color: b.tx1,
              fontSize: 15,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.01em',
            }}
          >
            {partner?.name || 'SAMA'}
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span className="sama-live-dot sama-live-dot--brasa" style={{ width: 6, height: 6 }} />
            <Text style={{ color: b.tx3, fontSize: 11, fontWeight: 500 }}>Panel aliado</Text>
          </div>
        </div>
      )}
      <Tooltip title={mini ? 'Expandir menú' : 'Colapsar menú'} placement="right">
        {collapseControl ? (
          <Button
            type="text"
            aria-label={mini ? 'Expandir menú' : 'Colapsar menú'}
            icon={
              mini ? (
                <DoubleRightOutlined style={{ fontSize: 13, color: b.tx3 }} />
              ) : (
                <DoubleLeftOutlined style={{ fontSize: 13, color: b.tx3 }} />
              )
            }
            onClick={() => (mini ? setCollapsed(false) : setCollapsed(true))}
            style={{ borderRadius: 8, width: 28, height: 28, flexShrink: 0 }}
          />
        ) : (
          <span style={{ width: 28, flexShrink: 0 }} />
        )}
      </Tooltip>
    </div>
  );

  // useNavigationMenu ya memoiza internamente: llamar directo, nunca dentro de useMemo
  const { menuItems, selectedKey, openKey } = useNavigationMenu();

  const renderSidebarContent = useCallback(
    // Estructura a prueba de desborde: marca fija → nav (única zona con
    // scroll, minHeight:0 para que flex la encoja) → footer fijo en flujo.
    // Sin absolute ni offsets: el logout siempre queda al fondo, visible.
    (onItemClick?: () => void, mini = false, showCollapse = true) => (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {renderBrand(mini, showCollapse)}
        <div
          className="sama-nav"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '12px 0 8px',
          }}
        >
          <NavigationMenu
            menuItems={menuItems}
            selectedKey={selectedKey}
            openKey={openKey}
            mode={b.isDark ? 'dark' : 'light'}
            backgroundColor="transparent"
            onItemClick={onItemClick}
          />
        </div>
        <div
          style={{
            flexShrink: 0,
            marginTop: 'auto',
            padding: 12,
            background: b.bgBase,
            borderTop: `1px solid ${b.lineSoft}`,
          }}
        >
          <Tooltip title={mini ? 'Cerrar sesión' : ''} placement="right">
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              block
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: mini ? 'center' : 'flex-start',
                gap: 10,
                height: 40,
                borderRadius: 10,
                fontWeight: 500,
              }}
            >
              {!mini && <span>Cerrar sesión</span>}
            </Button>
          </Tooltip>
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleLogout, menuItems, selectedKey, openKey, b.bgBase, b.lineSoft, partner?.name, collapsed]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ImpersonationBanner />

      <Drawer
        placement="left"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        size={264}
        styles={{ body: { padding: 0, background: b.bgBase } }}
      >
        {renderSidebarContent(() => setDrawerVisible(false), false, false)}
      </Drawer>

      <Layout style={{ flex: 1, background: 'transparent' }}>
        {!isMobile && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            collapsedWidth={76}
            theme={b.isDark ? 'dark' : 'light'}
            width={248}
            style={{
              background: b.bgBase,
              borderRight: `1px solid ${b.lineSoft}`,
            }}
          >
            {renderSidebarContent(undefined, collapsed)}
          </Sider>
        )}

        <Layout style={{ background: 'transparent' }}>
          <Header
            style={{
              padding: isMobile ? '0 12px' : '0 20px',
              background: b.isDark ? 'rgba(23,19,16,0.86)' : 'rgba(255,250,243,0.88)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              borderBottom: `1px solid ${b.lineSoft}`,
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              width: '100%',
              height: 64,
              lineHeight: '64px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {isMobile && (
                <Button
                  type="text"
                  aria-label="Abrir menú"
                  icon={<MenuOutlined style={{ color: b.tx1, fontSize: 19 }} />}
                  onClick={() => setDrawerVisible(true)}
                  style={{ borderRadius: 10, width: 40, height: 40 }}
                />
              )}
              <StoreSelector
                allowedStores={allowedStores}
                activeStoreId={activeStoreId}
                isSwitchingStore={isSwitchingStore}
                isMobile={isMobile}
                onSwitchStore={handleSwitchStore}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 10 }}>
              <ExchangeRateHeaderWidget isMobile={isMobile} />
              <StoreStatusPill isMobile={isMobile} />
              <ThemeSwitcher isMobile={isMobile} />
              <UserProfileDropdown partnerName={partner?.name} isMobile={isMobile} />
            </div>
          </Header>

          <Content
            className="sama-enter"
            style={{
              margin: isMobile ? '10px 10px 0' : '18px 20px 0',
              minHeight: 280,
              paddingBottom: 28,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </div>
  );
};

export default MainLayout;
